
import { usePinia, useSearchStore } from '#imports'
import { Bounds } from 'leaflet'
import { isEqual } from 'lodash'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { getWKTPointFromGeoPoint } from '~/utility/geo/GeoLocation'
import { GeoBounds } from '~/utility/geo/bounds'
import { getPointFromStringOrDefault, getWKTPoint } from '~/utility/geo/latLng'
import { isLocalStorageAvailable } from '~/utility/isLocalStorageAvailable'
import {
  MapSearchEngine,
  getMapSearchEngine,
} from '../../maps-search/domain/engine'
import { getCampsiteBySlug } from '../../maps-search/infrastructure/FetchCampsiteMapSearchService'
import {
  CampsiteMapsSearchResult,
  SearchFilters,
} from '../../maps-search/schemas'
import { isSearchUrlRoute } from '../../search/isSearchUrlComponent'
import { DEFAULT_BOUNDS_ENGLAND } from '../consts'
import {
  ResultCampsitesMapProps,
  ResultMapCampsite,
} from './campsites/CampsitesMapProps'

interface Point {
  lat: number
  lng: number
}

// setting this high until we fix abort
const UNDATED_SEARCH_RESULTS_LIMIT = 99999

function getCampsiteForMap(
  campsite: CampsiteMapsSearchResult,
): ResultMapCampsite {
  const { lat, lng } = getPointFromStringOrDefault(campsite.point)
  return {
    ...campsite,
    point: { lat, lng },
  }
}

@Component<MapsMain>({
  head: function () {
    return this.head()
  },
  setup() {
    // since we are doing a search anyway, let's use the search results to initilise the first set of markers
    const searchStore = useSearchStore(usePinia())
    return { searchStore }
  },
})
export default class MapsMain extends Vue {
  searchStore: ReturnType<typeof useSearchStore>
  mapSearchEngine: MapSearchEngine | null = null

  @Prop()
    searchFilters: SearchFilters

  @Prop({ required: false })
    visibleMarker?: string

  @Prop({ required: false })
    centre?: string

  resultsCount: number = 0
  searching = true
  campsites: ResultMapCampsite[] = []

  private point: Point | null = null

  newSearchBounds: '' | Bounds = ''
  private resultsBounds: GeoBounds | null = null
  currentPage = 0

  selectedCampsite: CampsiteMapsSearchResult | null = null
  loadingMarkerInfoWindow = false

  // this is the default value for the search as I move the map
  searchAsMapMoves = true
  private isRedoLabel = false

  get showRedoSearchButtonDesktop() {
    return !this.searchAsMapMoves && this.isRedoLabel
  }

  beforeMount() {
    if (
      isLocalStorageAvailable() &&
      localStorage.getItem('searchAsMapMoves') !== null
    ) {
      this.searchAsMapMoves =
        localStorage.getItem('searchAsMapMoves') === 'true'
    }
  }

  mounted() {
    const isDev = process.env.NODE_ENV !== 'production'
    if (isDev) console.log('MapsMain.mounted', this.$route.fullPath)
    if (this.mapSearchEngine) return
    if (isDev) console.log('creating mapSearchEngine')
    if (this.searchStore?.context?.results?.campsites?.length) {
      if (isDev) console.log('MapsMain using search campsites')
      // try use the first page of results (but props are slightly different)
      this.campsites =
        this.searchStore?.context?.results?.campsites.map(getCampsiteForMap) ||
        []
    }
    // start the engine and listeners
    this.mapSearchEngine = getMapSearchEngine(
      this.$route.params.lang || 'en-gb',
      this.$gtm,
    )
    this.registerSearchEngineOnContext()
    this.mapSearchEngine.start()
    this.search()
  }

  cleanup() {
    if (this.mapSearchEngine) {
      this.mapSearchEngine.stop()
      this.mapSearchEngine = null
    }
  }

  beforeDestroy() {
    this.cleanup()
  }

  beforeUnmount() {
    this.cleanup()
  }

  head() {
    if (this.$isDesktop) return {}
    return {
      title: (this.$t('{resultsCount} results shown on map', {
        resultsCount: this.resultsCount,
      }) + ' | Pitchup.com') as string,
    }
  }

  get searchFiltersForNavBar() {
    return {
      ...this.searchFilters,
      fields: [
        'campsite_id',
        'categories',
        'category_ids',
        'hierarchy_text_short',
        'id',
        'name',
        'path',
        'cheapest_price',
        'point',
        'primary_photo',
        'rate_count',
        'rating',
        'rating100',
        'slug',
        'url',
      ],
    }
  }

  get mapCentre(): string | undefined {
    if (this.centre) return this.centre
    if (this.searchHasNoLocationData) return this.mapCenterWKTPoint
    if (this.searchHasAreaData && this.searchFilters.area)
      return getWKTPointFromGeoPoint(this.searchFilters.area)
    return undefined
  }

  private get searchHasAreaData() {
    return this.searchFilters.area && !this.searchFilters.area.isEmptyPoint
  }

  private get mapCenterWKTPoint() {
    const point = this.$i18n.localeInfo.mapCenter
    return getWKTPoint(point.lat, point.lng)
  }

  private get searchHasNoLocationData() {
    return (
      !this.searchFilters.hierarchyPath &&
      !this.searchFilters.bounds &&
      (!this.searchFilters.area || this.searchFilters.area.isEmptyPoint)
    )
  }

  updateSearchBounds() {
    if (!this.newSearchBounds) {
      return
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {
      area,
      hierarchyPath,
      placename,
      previousPath,
      previousPoint,
      ...filters
    } = this.searchFilters
    const newFilters = {
      ...filters,
      area: undefined,
      bounds: this.newSearchBounds,
      hierarchyPath: undefined,
      previousPath: previousPath || hierarchyPath,
      previousPoint: previousPoint || { area, placename },
      page: undefined,
    }
    this.$emit('searchFiltersUpdated', newFilters)
    this.newSearchBounds = ''
  }

  toggleSearchAsMapMoves(event) {
    this.searchAsMapMoves = event
    if (isLocalStorageAvailable()) {
      localStorage.setItem(
        'searchAsMapMoves',
        this.searchAsMapMoves ? 'true' : 'false',
      )
    }
    if (event) {
      this.updateSearchBounds()
      this.isRedoLabel = false
    }
  }

  redoDesktopSearch() {
    this.searchAsMapMoves = false
    this.isRedoLabel = false
    this.updateSearchBounds()
  }

  setRedoLabel() {
    if (this.$isDesktop && !this.searchAsMapMoves && !this.isRedoLabel)
      this.isRedoLabel = true
  }

  updateNewSearchBounds(bounds: Bounds) {
    this.newSearchBounds = bounds
    if (this.$isDesktop && this.searchAsMapMoves) {
      this.updateSearchBounds()
    }
    this.setRedoLabel()
  }

  async updateSelectedCampsite(slug: string) {
    // When performing dated search we have all the data we need for the popup
    if (this.searchFilters.dates) {
      this.selectedCampsite =
        this.campsites.find((c) => c.slug === slug) || null
      return
    }
    this.loadingMarkerInfoWindow = true
    this.selectedCampsite = null
    const createGtmPerfEvent =
      window.performance && !window.navigator.userAgent.includes('gha2spon')
    if (createGtmPerfEvent) {
      window.performance.mark('infowindow-load-started')
    }
    const campsite = await this.fetchCampsiteById(slug)
    if (createGtmPerfEvent) {
      window.performance.mark('infowindow-load-complete')
      const loadDuration = window.performance.measure(
        'infowindow-load-duration',
        'infowindow-load-started',
        'infowindow-load-complete',
      )
      this.$gtm.push({
        event: 'map info window search duration',
        mapsSearchDuration: loadDuration?.duration,
        networkEffectiveType: window.navigator.connection?.effectiveType,
      })
    }

    this.selectedCampsite = campsite
    this.loadingMarkerInfoWindow = false
    this.$emit('mapMarkerOpened', slug)
  }

  fetchCampsiteById(slug) {
    return getCampsiteBySlug(slug, this.$route.params.lang, this.$gtm)
  }

  @Watch('searchFilters')
  onSearchFiltersChange(newVal, oldVal) {
    if (isEqual(newVal, oldVal)) return
    this.search()
  }

  search() {
    if (!isSearchUrlRoute(this.$route)) {
      return
    }
    this.mapSearchEngine?.search({
      ...this.searchFilters,
      limit: UNDATED_SEARCH_RESULTS_LIMIT,
      page: 1,
    })
  }

  get isDated() {
    return !!this.searchFilters.dates
  }

  get mapProps(): ResultCampsitesMapProps {
    return {
      campsites: this.campsites,
      initialBounds: this.centre ? undefined : this.mapInitialBounds,
      visibleMarker: this.visibleMarker,
      centre: this.mapCentre,
      circle: this.circle,
      useClustering: true,
      searchFilters: this.searchFilters,
    }
  }

  get circle() {
    return this.point
      ? {
        lat: this.point.lat,
        lng: this.point.lng,
        rangeMiles: this.searchFilters.area
          ? this.searchFilters.area?.rangeMiles
          : this.$config.public.defaultWithinRange,
        isEmptyPoint: false,
      }
      : null
  }

  get mapInitialBounds(): GeoBounds {
    return this.searchFilters.bounds
      ? this.searchFilters.bounds
      : this.resultsBounds || DEFAULT_BOUNDS_ENGLAND
  }

  get showRedoSearchButton() {
    return (this.newSearchBounds || this.errorOccurred) && !this.searching
  }

  get errorOccurred() {
    return this.mapSearchEngine?.machine.state.context.errorOccurred
  }

  private registerSearchEngineOnContext() {
    this.mapSearchEngine?.machine.onTransition((state, event) => {
      if (process.env.NODE_ENV !== 'production')
        console.log(
          'MapsMain.onTransition',
          state.toStrings(),
          state.context?.page,
          event.type,
          state.context?.campsites?.length,
        )
      this.searching = state.matches('searching')
      const context = state.context
      const campsites = (context.campsites || []).map(getCampsiteForMap)
      // this is so the results aren't cleared when doing a new search
      // until it has something to show or it has finished and no results
      if (
        !(
          state.matches('results') ||
          (state.matches('searching') && campsites.length)
        )
      )
        return
      if (
        campsites.length &&
        JSON.stringify(this.resultsBounds) !==
          JSON.stringify(context.results?.bounds)
      ) {
        this.resultsBounds = context.results?.bounds || null
      }
      const path = context.results?.path || ''
      const hierarchyPath = context.filters.hierarchyPath || ''
      this.redirectToFixedPath(hierarchyPath, path)
      this.campsites = campsites
      this.resultsCount = campsites.length
      this.point = context?.results?.point || null
    })
  }

  private redirectToFixedPath(hierarchyPath: string, path: string) {
    if (hierarchyPath && path && hierarchyPath !== path) {
      const redirectPath = this.$route.fullPath.replace(hierarchyPath, path)
      this.$nuxt.context.redirect(redirectPath)
    }
  }
}
